import { useToast } from '@chakra-ui/react';
import { startOfISOWeek, startOfToday } from 'date-fns';
import React, { useState } from 'react';

import {
  CmBox,
  CmButton,
  CmCard,
  CmFlex,
  CmLabelAndSelect,
  CmStack,
  CmText,
  ReloadIcon,
} from '@/components/uiParts';
import {
  OrdersDataCSVTypes,
  OrdersDataDateTypes,
} from '@/domain/models/Order/ordersData';
import { DateTimeRangeForm } from '@/pages/ordersData/components/DateTimeRangeForm';

export interface OrdersDataFilter {
  from: Date;
  to: Date;
  startHour: number;
  startMinutes: number;
  endHour: number;
  endMinutes: number;
  selectedCSVType: string;
  selectedDateTimeType: string;
}

interface CSVOrderFilterFormProps {
  dateTypes?: Record<string, string>;
  onSubmit?: (filter: OrdersDataFilter) => void;
  onRefetchCSVJobs?: () => Promise<unknown>;
  isLoading?: boolean;
}

export const CSVOrderFilterForm: React.FC<CSVOrderFilterFormProps> = ({
  dateTypes,
  onSubmit,
  onRefetchCSVJobs,
  isLoading,
}) => {
  const [selectedCSVType, setSelectedCSVType] = useState<string>(
    OrdersDataCSVTypes.OrdersList
  );
  const [selectedDateTimeType, setSelectedDateTimeType] = useState<string>(
    dateTypes?.[0] || OrdersDataDateTypes.EstimatedPickupTime
  );
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(
    startOfISOWeek(new Date())
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(startOfToday());
  const [isRefetching, setIsRefetching] = useState(false);

  const toast = useToast();

  const handleSelectCSVType: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    const csvTypeName = e.target.value;
    setSelectedCSVType(csvTypeName);
  };

  const handleSelectDateTimeType: React.ChangeEventHandler<
    HTMLSelectElement
  > = (e) => {
    const dateTimeTypeName = e.target.value;
    setSelectedDateTimeType(dateTimeTypeName);
  };

  const submit: React.FormEventHandler = (e) => {
    e.preventDefault();
    const filters: OrdersDataFilter = {
      from: selectedStartDate,
      to: selectedEndDate,
      startHour: selectedStartDate.getHours(),
      startMinutes: selectedStartDate.getMinutes(),
      endHour: selectedEndDate.getHours(),
      endMinutes: selectedEndDate.getMinutes(),
      selectedCSVType,
      selectedDateTimeType,
    };

    if (onSubmit) {
      onSubmit(filters);
      toast({
        title: 'CSV作成ジョブが開始されました。',
        description:
          '指定期間によってCSVの出力に時間がかかる可能性があります。',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleRefetch = async () => {
    setIsRefetching(true);
    try {
      await onRefetchCSVJobs?.();
      toast({
        title: 'CSVダウンロード状況を更新しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsRefetching(false);
    }
  };

  return (
    <CmBox>
      <CmCard>
        <CmStack spacing={4} mt={0}>
          <CmLabelAndSelect
            selectWidth="300px"
            label="CSV種別"
            fontWeight="bold"
            onChange={handleSelectCSVType}
            value={selectedCSVType}
          >
            {Object.values(OrdersDataCSVTypes).map((csvType) => (
              <option value={csvType} key={csvType}>
                {csvType}
              </option>
            ))}
          </CmLabelAndSelect>
          <CmLabelAndSelect
            selectWidth="300px"
            label="日時種別"
            fontWeight="bold"
            onChange={handleSelectDateTimeType}
            value={selectedDateTimeType}
          >
            {Object.values(dateTypes || OrdersDataDateTypes).map(
              (dateTimeType) => (
                <option value={dateTimeType} key={dateTimeType}>
                  {dateTimeType}
                </option>
              )
            )}
          </CmLabelAndSelect>
          <CmFlex align="center" justify="space-between">
            <DateTimeRangeForm
              label="対象日時"
              startDate={selectedStartDate}
              setStartDate={setSelectedStartDate}
              endDate={selectedEndDate}
              setEndDate={setSelectedEndDate}
              // 2ヶ月後まで
              maxDate={new Date(new Date().setMonth(new Date().getMonth() + 2))}
              // 2ヶ月前まで
              minDate={new Date(new Date().setMonth(new Date().getMonth() - 2))}
            />
          </CmFlex>
          <CmFlex justify="end">
            <CmStack spacing={1} align="flex-end">
              {/* @ts-ignore TS2590 */}
              <CmButton size="lg" onClick={submit} disabled={isLoading}>
                CSV作成
              </CmButton>
              <CmText fontSize="sm">
                ダウンロードの有効期限はCSV作成後1時間です
              </CmText>
            </CmStack>
          </CmFlex>
        </CmStack>
      </CmCard>
      <CmFlex justify="end" mt={2}>
        <CmButton
          leftIcon={<ReloadIcon />}
          onClick={handleRefetch}
          variant="outline"
          size="md"
          isLoading={isRefetching}
        >
          CSVダウンロード状況を更新
        </CmButton>
      </CmFlex>
    </CmBox>
  );
};
